import React, {
  memo,
  forwardRef,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {Platform, ScrollView, View} from 'react-native';

import styled from 'styled-components/native';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import {useFocusEffect} from '@react-navigation/native';

interface ContentScrollViewProps {
  isWeb?: boolean;
  children?: React.ReactNode;
  scrollToEnd?: boolean;
  scrollEnabled?: boolean;
}

const ContentScrollView = (props: ContentScrollViewProps, ref = null) => {
  const {isWeb = false, children, scrollEnabled = true} = props;
  const scrollViewRef = useRef<ScrollView>(null);
  const viewRef = useRef<View>(null);

  const {setCanRefresh} = usePullDownRefreshContext();

  const StyledScrollView = styled.View<{scrollEnabled?: boolean}>`
    overflow-x: hidden;
    overflow-y: ${({scrollEnabled}) =>
      scrollEnabled === false ? 'hidden' : 'auto'};
    height: 100%;
  `;

  useEffect(() => {
    const currentRef: any = viewRef.current;

    if (Platform.OS === 'web' && props.scrollToEnd) {
      currentRef?.addEventListener('DOMNodeInserted', (event: any) => {
        const {currentTarget: target} = event;

        // Added an id for the web-player since it also triggers the DOMNodeInserted
        // event. So, if there is no id, should proceed with the autoscroll.
        if (!event.target.id) {
          if (target.scrollHeight === 0) {
            setTimeout(() => {
              target.scroll({
                top: target.scrollHeight,
                behavior: 'smooth',
              });
            }, 1000);
          } else {
            setTimeout(() => {
              target.scroll({
                top: target.scrollHeight,
                behavior: 'smooth',
              });
            }, 10);
          }
        }
      });
    }

    return () => {
      currentRef?.removeEventListener('DOMNodeInserted', () => {});
    };
  }, [props.scrollToEnd]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <>
      {isWeb ? (
        <StyledScrollView ref={viewRef} scrollEnabled={props.scrollEnabled}>
          {children}
        </StyledScrollView>
      ) : (
        <ScrollView
          onScroll={handleScroll}
          onLayout={e => {
            setScrollViewHeight(e.nativeEvent.layout.height);
          }}
          onContentSizeChange={(contentWidth, contentHeight) => {
            setContentHeight(contentHeight);
            if (props.scrollToEnd) {
              scrollViewRef.current?.scrollToEnd({animated: true});
            }
          }}
          ref={scrollViewRef}>
          {children}
        </ScrollView>
      )}
    </>
  );
};

export default memo(ContentScrollView);
